import React, { useEffect, useState } from 'react';

import { Autocomplete, BasicDialog, TextField } from '@opendoor/mosaic';

import { SearchVisits_trippy_searchVisits_visits } from '../../graphql/athena/types/SearchVisits';
import { useAssignVisit } from '../../graphql/mutations/trippy/useAssignVisit';
import useGetVendorBranchOrgMembers from '../../graphql/queries/bender/useGetVendorBranchOrgMembers';
import useGetVisitsWithOrgMembers from '../../graphql/queries/trippy/useGetVisitsWithOrgMembers';
import { DAY_ONE_WALK_PARTNERS_NATIONAL_BRANCH_ID } from '../../utils/constants';
import VerticalLayout from '../layouts/VerticalLayout';

interface AssigneeItem {
  label: string; // user name
  item: string; // user token
}

interface AssignVisitModalProps {
  visible: [
    SearchVisits_trippy_searchVisits_visits | null,
    React.Dispatch<React.SetStateAction<SearchVisits_trippy_searchVisits_visits | null>>,
  ];
}

const AssignVisitModal = ({ visible }: AssignVisitModalProps) => {
  const [visit, setVisit] = visible;
  const [selectedAssignee, setSelectedAssignee] = useState<AssigneeItem | null>(null);
  const [assignVisit] = useAssignVisit();

  useEffect(() => {
    if (!visit) setSelectedAssignee(null);
  }, [visit]);

  // get the members of the Day One Walk Partners - National organization
  const { data: pd1wOrgData } = useGetVendorBranchOrgMembers({
    variables: { id: DAY_ONE_WALK_PARTNERS_NATIONAL_BRANCH_ID },
  });
  const pd1WMembers =
    pd1wOrgData?.vendor.getVendorBranchByID?.vendorBranch?.organization?.members || [];

  const { data } = useGetVisitsWithOrgMembers({
    variables: {
      visitIds: visit ? [visit!.id] : [],
    },
    skip: !visit,
  });

  const thisOrgMembers = data?.trippy.getVisits?.visits
    ? data.trippy.getVisits.visits.map((v) => v.assignedOrganization?.members).flat()
    : [];
  const enabledMembers = thisOrgMembers.filter((m) => {
    if (!m) return false;
    return !!pd1WMembers.find((pd1wMember) => pd1wMember.userToken === m.userToken);
  });
  const enabledAssigneeItems: AssigneeItem[] = [];
  enabledMembers.forEach((m) => {
    if (m && m.user) {
      enabledAssigneeItems.push({ label: m.user.fullName, item: m.userToken });
    }
  });

  const onAssignClick = async () => {
    if (!!visit && !!selectedAssignee) {
      await assignVisit({
        variables: {
          id: visit.id,
          userToken: selectedAssignee.item,
        },
        refetchQueries: ['SearchVisits'],
      });
      setVisit(null);
    }
  };

  const dialogContent = (
    <VerticalLayout style={{ height: '20vh' }}>
      <span style={{ marginBottom: 20, fontWeight: '600' }}>
        Choose an individual to complete the job
      </span>
      <Autocomplete
        data-testid="assign-visit-modal-autocomplete"
        multiple={false}
        options={enabledAssigneeItems}
        value={selectedAssignee}
        getOptionLabel={(o) => o.label}
        getOptionSelected={(option, value) => {
          return option.item === value.item;
        }}
        onChange={(_, value) => setSelectedAssignee(value)}
        renderInput={(params) => (
          <TextField {...params} placeholder="Enter name" color="secondary" fullWidth />
        )}
      ></Autocomplete>
    </VerticalLayout>
  );

  return (
    <BasicDialog
      title="Assign Job"
      open={!!visit}
      content={dialogContent}
      confirmLabel={'Assign'}
      showCancelButton
      onCancel={() => setVisit(null)}
      onConfirm={() => onAssignClick()}
      confirmDisabled={!selectedAssignee}
      dialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
    />
  );
};

export default AssignVisitModal;
